import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { h2hAPI, matchAPI } from '../api/endpoints';
import { formatDate, formatXPUNT } from '../utils/helpers';
import { 
  FaArrowLeft, FaFutbol, FaSearch, FaGlobe, 
  FaClock, FaMapPin, FaCheck, FaEdit, 
  FaInfoCircle, FaHandshake
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './CreateH2H.css';

const CreateH2H = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchSearchTerm, setMatchSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    minimum_stake: '',
  });

  const isPunterPlus = user?.punter_plus || false;

  // Fetch upcoming matches on mount
  useEffect(() => {
    if (!isPunterPlus) {
      toast.error('You need Punter+ to create H2H games');
      navigate('/subscription');
      return;
    }
    
    fetchUpcomingMatches();
  }, [isPunterPlus, navigate]);

  // ==================== FETCH UPCOMING MATCHES ====================
  const fetchUpcomingMatches = async () => {
    setLoadingMatches(true);
    try {
      const response = await matchAPI.getUpcoming();
      console.log('Upcoming matches response:', response.data);
      
      // Handle different response structures
      let matches = [];
      if (Array.isArray(response.data)) {
        matches = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        matches = response.data.data;
      } else if (response.data?.fixtures && Array.isArray(response.data.fixtures)) {
        matches = response.data.fixtures;
      } else if (response.data?.matches && Array.isArray(response.data.matches)) {
        matches = response.data.matches;
      }
      
      console.log('Parsed upcoming matches:', matches);
      setUpcomingMatches(matches);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      toast.error('Failed to load matches');
      setUpcomingMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  // ==================== FILTER MATCHES ====================
  const filteredMatches = React.useMemo(() => {
    return upcomingMatches.filter(match => {
      if (!matchSearchTerm) return true;
      const search = matchSearchTerm.toLowerCase();
      return (
        match.home_team?.toLowerCase().includes(search) ||
        match.away_team?.toLowerCase().includes(search) ||
        match.home_club_name?.toLowerCase().includes(search) ||
        match.away_club_name?.toLowerCase().includes(search) ||
        match.league?.toLowerCase().includes(search) ||
        match.competition_name?.toLowerCase().includes(search)
      );
    });
  }, [upcomingMatches, matchSearchTerm]);

  // ==================== TEAM LOGO HELPER ====================
  const getTeamLogo = (match, teamType = 'home') => {
    if (!match) return null;
    if (teamType === 'home') {
      return match.home_club_logo || match.home_logo || null;
    }
    return match.away_club_logo || match.away_logo || null;
  };

  // ==================== HANDLE SELECT MATCH ====================
  const handleSelectMatch = (match) => {
    setSelectedMatch(match);
    
    // Auto-generate a title
    const homeTeam = match.home_team || match.home_club_name || '';
    const awayTeam = match.away_team || match.away_club_name || '';
    
    setFormData(prev => ({
      ...prev,
      title: prev.title || `${homeTeam} vs ${awayTeam}`,
    }));
  };

  const handleClearMatch = () => {
    setSelectedMatch(null);
  };

  // ==================== HANDLE FORM CHANGE ====================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ==================== HANDLE SUBMIT ====================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedMatch) {
      toast.error('Please select a match first');
      return;
    }

    if (!formData.minimum_stake || parseFloat(formData.minimum_stake) <= 0) {
      toast.error('Please enter a valid minimum stake');
      return;
    }

    setLoading(true);
    try {
      const matchId = selectedMatch.id || selectedMatch.fixture_id;
      const homeTeam = selectedMatch.home_team || selectedMatch.home_club_name || '';
      const awayTeam = selectedMatch.away_team || selectedMatch.away_club_name || '';
      
      const h2hData = {
        title: formData.title || `${homeTeam} vs ${awayTeam}`,
        home_team: homeTeam,
        away_team: awayTeam,
        fixture_id: matchId,
        league: selectedMatch.league || selectedMatch.competition_name || '',
        venue: selectedMatch.venue || '',
        kickoff_time: selectedMatch.kickoff_time || '',
        start_time: selectedMatch.kickoff_time || '',
        minimum_stake: parseFloat(formData.minimum_stake),
        home_club_logo: getTeamLogo(selectedMatch, 'home'),
        away_club_logo: getTeamLogo(selectedMatch, 'away'),
      };

      const response = await h2hAPI.create(h2hData);
      
      if (response.data.success) {
        toast.success('H2H game created successfully!');
        navigate(`/h2h/${response.data.id}`);
      }
    } catch (error) {
      console.error('Create H2H error:', error);
      toast.error(error.response?.data?.message || 'Failed to create H2H game');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-h2h-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="create-h2h-card">
        <div className="card-header">
          <h1><FaHandshake /> Create H2H Game</h1>
          <p className="card-subtitle">
            Select a match from upcoming fixtures and set your stake
          </p>
          <span className="punter-badge">⭐ Punter+</span>
        </div>

        <form onSubmit={handleSubmit} className="create-h2h-form">
          {/* ==================== MATCH SELECTION ==================== */}
          {!selectedMatch ? (
            <div className="match-selection-section">
              <div className="section-title">
                <FaFutbol className="section-icon" />
                <span>Select Match from Upcoming Fixtures</span>
              </div>

              <div className="search-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search matches by team or league..."
                  value={matchSearchTerm}
                  onChange={(e) => setMatchSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {loadingMatches ? (
                <div className="loading-matches">
                  <LoadingSpinner size="small" />
                  <span>Loading matches...</span>
                </div>
              ) : upcomingMatches.length === 0 ? (
                <div className="empty-state-small">
                  <FaFutbol className="empty-icon" />
                  <p>No upcoming matches available</p>
                  <small>Ask admin to add matches first</small>
                </div>
              ) : filteredMatches.length === 0 ? (
                <div className="empty-state-small">
                  <FaSearch className="empty-icon" />
                  <p>No matches found matching "{matchSearchTerm}"</p>
                  <small>Try a different search term</small>
                </div>
              ) : (
                <div className="matches-selection-list">
                  {filteredMatches.map((match) => {
                    const matchId = match.id || match.fixture_id;
                    const homeTeam = match.home_team || match.home_club_name;
                    const awayTeam = match.away_team || match.away_club_name;
                    const homeLogo = getTeamLogo(match, 'home');
                    const awayLogo = getTeamLogo(match, 'away');
                    const league = match.league || match.competition_name;
                    
                    return (
                      <div 
                        key={matchId}
                        className="match-selection-item"
                        onClick={() => handleSelectMatch(match)}
                      >
                        <div className="match-teams-select">
                          <div className="team-with-logo">
                            {homeLogo ? (
                              <img 
                                src={homeLogo} 
                                alt={homeTeam} 
                                className="team-logo-select"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <span className="team-logo-placeholder">
                                {(homeTeam || 'H').charAt(0).toUpperCase()}
                              </span>
                            )}
                            <span className="home-team">{homeTeam}</span>
                          </div>
                          
                          <span className="vs">vs</span>
                          
                          <div className="team-with-logo">
                            {awayLogo ? (
                              <img 
                                src={awayLogo} 
                                alt={awayTeam} 
                                className="team-logo-select"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <span className="team-logo-placeholder">
                                {(awayTeam || 'A').charAt(0).toUpperCase()}
                              </span>
                            )}
                            <span className="away-team">{awayTeam}</span>
                          </div>
                        </div>
                        
                        <div className="match-info-select">
                          {league && (
                            <span className="match-league">
                              <FaGlobe /> {league}
                            </span>
                          )}
                          {match.kickoff_time && (
                            <span className="match-time">
                              <FaClock /> {formatDate(match.kickoff_time)}
                            </span>
                          )}
                          {match.venue && (
                            <span className="match-venue">
                              <FaMapPin /> {match.venue}
                            </span>
                          )}
                        </div>
                        
                        <button type="button" className="select-match-btn">
                          Select <FaCheck />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* ==================== SELECTED MATCH INFO ==================== */}
              <div className="selected-match-info">
                <div className="selected-match-header">
                  <span className="selected-label">
                    <FaCheck /> Match Selected
                  </span>
                  <button 
                    type="button"
                    className="change-match-btn"
                    onClick={handleClearMatch}
                  >
                    <FaEdit /> Change Match
                  </button>
                </div>
                <div className="selected-match-content">
                  <div className="selected-teams">
                    <div className="selected-team">
                      {getTeamLogo(selectedMatch, 'home') ? (
                        <img 
                          src={getTeamLogo(selectedMatch, 'home')} 
                          alt={selectedMatch.home_team || selectedMatch.home_club_name} 
                          className="team-logo-selected"
                        />
                      ) : (
                        <span className="team-logo-placeholder selected">
                          {(selectedMatch.home_team || selectedMatch.home_club_name || 'H').charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span className="team">
                        {selectedMatch.home_team || selectedMatch.home_club_name}
                      </span>
                    </div>
                    
                    <span className="vs">vs</span>
                    
                    <div className="selected-team">
                      {getTeamLogo(selectedMatch, 'away') ? (
                        <img 
                          src={getTeamLogo(selectedMatch, 'away')} 
                          alt={selectedMatch.away_team || selectedMatch.away_club_name} 
                          className="team-logo-selected"
                        />
                      ) : (
                        <span className="team-logo-placeholder selected">
                          {(selectedMatch.away_team || selectedMatch.away_club_name || 'A').charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span className="team">
                        {selectedMatch.away_team || selectedMatch.away_club_name}
                      </span>
                    </div>
                  </div>
                  
                  <div className="selected-meta">
                    {(selectedMatch.league || selectedMatch.competition_name) && (
                      <span><FaGlobe /> {selectedMatch.league || selectedMatch.competition_name}</span>
                    )}
                    {selectedMatch.kickoff_time && (
                      <span><FaClock /> {formatDate(selectedMatch.kickoff_time)}</span>
                    )}
                    {selectedMatch.venue && (
                      <span><FaMapPin /> {selectedMatch.venue}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* ==================== FORM FIELDS ==================== */}
              <div className="form-group">
                <label>Title (Optional)</label>
                <input
                  type="text"
                  name="title"
                  placeholder="e.g., El Clasico H2H"
                  value={formData.title}
                  onChange={handleChange}
                />
                <small className="field-hint">
                  Leave blank to use "{selectedMatch.home_team || selectedMatch.home_club_name} vs {selectedMatch.away_team || selectedMatch.away_club_name}"
                </small>
              </div>

              <div className="form-group">
                <label>Minimum Stake ($XPUNT) *</label>
                <input
                  type="number"
                  name="minimum_stake"
                  placeholder="e.g., 5.00"
                  value={formData.minimum_stake}
                  onChange={handleChange}
                  step="0.01"
                  min="0.01"
                  required
                />
                <small className="field-hint">
                  Minimum amount required to join this game
                </small>
              </div>

              <div className="form-info-box">
                <FaInfoCircle className="info-icon" />
                <div>
                  <strong>How H2H Works:</strong>
                  <ul className="info-list">
                    <li>Players predict the match outcome</li>
                    <li>Minimum stake: your set amount</li>
                    <li>When match ends, admin updates the score</li>
                    <li>Winning side splits the total pot</li>
                  </ul>
                </div>
              </div>
            </>
          )}

          {/* ==================== SUBMIT ==================== */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading || !selectedMatch}
            >
              {loading ? <LoadingSpinner size="small" /> : (
                <>
                  <FaHandshake /> Create H2H Game
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateH2H;